// filepath: src/components/inventory/InventoryTable.js
import React, { useState } from "react";
import { useAuth } from '../../../hooks/useAuth';
import EmptyState from '../../CommonComponents/EmptyState';
import { Package } from 'lucide-react';
import ProductImageFallback from '../../CommonComponents/ProductImageFallback';
import EditStockModal from "./EditStockModal";
import AddStockModal from "./AddStockModal";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from '../../../utils/tableStyle';

const formatQuantity = (qty) => {
    const value = Number(qty);
    if (!Number.isFinite(value)) return "0";
    return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/\.?0+$/, "");
};

const formatReleased = (qty) => {
    if (qty === null || qty === undefined) return "0";
    return formatQuantity(qty);
};

// Helper component for image fallback
const InventoryImage = ({ image_url, name, uploadsBaseURL }) => {
    const [src, setSrc] = React.useState(image_url ? `${uploadsBaseURL}/${image_url}` : null);

    return (
        <div className="relative h-10 w-10">
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-10 w-10 object-cover rounded border"
                    onError={() => setSrc(null)}
                />
            ) : (
                <ProductImageFallback className="h-10 w-10 rounded" aria-label="No product image" />
            )}
        </div>
    );
};

export default function InventoryTable({
    inventories = [],
    uploadsBaseURL,
    onEditStock,
    onEditReleasedStock,
    onReleaseToBranch,
    selectedBranchName,
    canRelease = false,
    typeFilter = "PRODUCT",
}) {
    const showCategoryColumn = typeFilter !== "RAW_MATERIAL";
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const { user } = useAuth();
    const isManager = user && user.user_type === 2 && String(user.role_name || '').toLowerCase() === 'manager';

    // Handler for edit icon
    const handleEdit = (inventory) => {
        setSelectedInventory(inventory);
        setEditModalOpen(true);
    };

    const handleAddStock = (inventory) => {
        setSelectedInventory(inventory);
        setAddModalOpen(true);
    };

    // Handler for modal close
    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleCloseAddModal = () => {
        setAddModalOpen(false);
        setSelectedInventory(null);
    };

    const handleSaveAddStock = (addQuantity, remarks) => {
        if (onReleaseToBranch && selectedInventory) {
            onReleaseToBranch(selectedInventory, addQuantity, remarks);
        }
        setAddModalOpen(false);
        setSelectedInventory(null);
    };

    const handleSaveAvailableStock = (newQuantity) => {
        if (onEditStock && selectedInventory) {
            onEditStock(selectedInventory, newQuantity);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleSaveReleasedStock = (newReleasedQty) => {
        if (onEditReleasedStock && selectedInventory) {
            onEditReleasedStock(selectedInventory, newReleasedQty);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    return (
        <>
            <div className={tableContainer}>
                <table className={tableBase}>
                    <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
                        <tr>
                            <th className={tableHeader + ' text-center'}>Image</th>
                            <th className={tableHeader + ' text-center'}>Name</th>
                            {showCategoryColumn && (
                                <th className={tableHeader + ' text-center'}>Category</th>
                            )}
                            <th className={tableHeader + ' text-center'}>Type</th>
                            <th className={tableHeader + ' text-center'}>Unit</th>
                            <th className={tableHeader + ' text-center'}>Stocks Available</th>
                            <th className={tableHeader + ' text-center'}>Stocks Released</th>
                            <th className={tableHeader + ' text-center'}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inventories.length === 0 ? (
                            <tr>
                                <td colSpan={showCategoryColumn ? 8 : 7} className={tableCell + ' text-center py-8'}>
                                    <div className="flex flex-col items-center justify-center">
                                        <EmptyState icon={Package} message="No inventory found." />
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            inventories.map(inv => {
                                const name = inv.name || inv.product_name || inv.raw_material_name || '-';
                                const category = inv.category || '-';
                                const type = inv.product_id ? 'Product' : inv.raw_material_id ? 'Raw Material' : '-';
                                const unit = inv.unit_type || inv.unit || '-';
                                return (
                                    <tr key={inv.id} className={tableRow + ' border-b last:border-0'}>
                                        <td className={tableCell + ' text-center'}>
                                            <InventoryImage
                                                image_url={inv.image_url}
                                                name={name}
                                                uploadsBaseURL={uploadsBaseURL}
                                            />
                                        </td>
                                        <td className={tableCell + ' text-center'}>{name}</td>
                                        {showCategoryColumn && (
                                            <td className={tableCell + ' text-center'}>{category}</td>
                                        )}
                                        <td className={tableCell + ' text-center'}>{type}</td>
                                        <td className={tableCell + ' text-center'}>{unit}</td>
                                        <td className={tableCell + ' text-center'}>{formatQuantity(inv.quantity)} {unit !== '-' ? unit : ''}</td>
                                        <td className={tableCell + ' text-center'}>
                                            {formatReleased(inv.released_quantity)} {unit !== '-' ? unit : ''}
                                        </td>
                                        <td className={tableCell + ' text-center'}>
                                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    disabled={isManager}
                                                    onClick={() => handleEdit(inv)}
                                                    className="px-3 py-1 text-xs font-semibold rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Adjust stock"
                                                >
                                                    Adjust
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={!canRelease}
                                                    onClick={() => handleAddStock(inv)}
                                                    className="px-3 py-1 text-xs font-semibold rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                    style={{
                                                        background: 'var(--color-primary)',
                                                        transition: 'background 0.2s',
                                                    }}
                                                    onMouseOver={e => e.currentTarget.style.background = 'var(--color-active)'}
                                                    onMouseOut={e => e.currentTarget.style.background = 'var(--color-primary)'}
                                                    title={canRelease
                                                        ? `Release stock${selectedBranchName ? ` to ${selectedBranchName}` : ''}`
                                                        : "Select a branch tab to release stock"}
                                                >
                                                    Release
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <EditStockModal
                isOpen={editModalOpen}
                onClose={handleCloseModal}
                inventory={selectedInventory}
                onSaveAvailable={handleSaveAvailableStock}
                onSaveReleased={handleSaveReleasedStock}
                canEditReleased={canRelease}
                branchLabel={selectedBranchName}
            />
            <AddStockModal
                isOpen={addModalOpen}
                onClose={handleCloseAddModal}
                inventory={selectedInventory}
                onSave={handleSaveAddStock}
                mode="release"
                branchLabel={selectedBranchName}
            />
        </>
    );
}
